/**
 * Payment Service - Razorpay Integration
 * WEB-ONLY: Handles subscription payment processing using Razorpay payment gateway
 * This service is exclusively for web platform payments
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

class PaymentService {
  constructor() {
    this.serviceName = 'PaymentService';
    this.isInitialized = false;
    this.razorpayKey = null;
    this.functions = null;
  }

  /**
   * Initialize payment service with Razorpay key (WEB-ONLY)
   * @param {Object} config - Payment configuration
   */
  async initialize(config = {}) {
    try {
      this.razorpayKey = config.razorpayKey || process.env.REACT_APP_RAZORPAY_KEY_ID;
      
      if (!this.razorpayKey) {
        throw new Error('Razorpay Key ID not configured. Set REACT_APP_RAZORPAY_KEY_ID in environment variables.');
      }

      // Initialize Firebase Functions
      this.functions = getFunctions();
      this.isInitialized = true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create Razorpay order via backend (WEB-ONLY)
   * @param {Object} upgradeData - Upgrade data with pricing
   * @returns {Object} Razorpay order details
   */
  async createPaymentIntent(upgradeData) {
    if (!this.isInitialized) {
      throw new Error('Payment service not initialized');
    }

    try {
      const createRazorpayOrder = httpsCallable(this.functions, 'createRazorpayOrder');

      const amount = upgradeData.pricing.totalPrice; // Amount in INR (not paise)

      // Razorpay requires receipt to be max 40 characters
      const shortOrgId = upgradeData.organizationId ? upgradeData.organizationId.substring(0, 8) : 'org';
      const shortPlanId = upgradeData.planId ? upgradeData.planId.substring(0, 8) : 'plan';
      const shortTimestamp = Date.now().toString().slice(-6); // Last 6 digits
      const receipt = `${shortOrgId}_${shortPlanId}_${shortTimestamp}`;

      const result = await createRazorpayOrder({
        amount: amount,
        currency: 'INR',
        receipt: receipt,
        organizationId: upgradeData.organizationId,  // Important: needed for role verification
        notes: {
          userId: upgradeData.userId,
          planId: upgradeData.planId,
          planName: upgradeData.planName,
          billingInterval: upgradeData.billingInterval
        }
      });

      return {
        orderId: result.data.orderId,
        amount: result.data.amount,
        currency: result.data.currency,
        receipt: result.data.receipt
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Open Razorpay checkout and process payment (WEB-ONLY)
   * @param {Object} paymentOptions - Payment options
   * @returns {Promise} Payment result
   */
  async processPayment(paymentOptions) {
    if (!window.Razorpay) {
      throw new Error('Razorpay Checkout script not loaded');
    }

    return new Promise((resolve, reject) => {
      const options = {
        key: this.razorpayKey,
        amount: paymentOptions.amount * 100, // Convert INR to paise (e.g., 499 to 49900)
        currency: 'INR',
        name: 'Ordease',
        description: paymentOptions.description || 'Subscription Payment',
        order_id: paymentOptions.orderId,
        handler: async function (response) {
          // STEP 1.3: Success callback
          try {
            resolve({
              success: true,
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              signature: response.razorpay_signature
            });
          } catch (error) {
            reject(error);
          }
        },
        prefill: {
          name: paymentOptions.name || 'Customer',
          email: paymentOptions.email || '',
          contact: paymentOptions.contact || ''
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: function() {
            reject(new Error('Payment cancelled - User closed payment window'));
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    });
  }

  /**
   * Verify payment signature and update subscription (WEB-ONLY)
   * @param {Object} paymentResponse - Payment response from Razorpay
   * @param {Object} subscriptionData - Subscription data
   * @returns {Object} Verification result
   */
  async verifyPayment(paymentResponse, subscriptionData) {
    if (!this.isInitialized) {
      throw new Error('Payment service not initialized');
    }

    try {
      const verifyRazorpayPayment = httpsCallable(this.functions, 'verifyRazorpayPayment');

      const result = await verifyRazorpayPayment({
        paymentId: paymentResponse.paymentId,
        orderId: paymentResponse.orderId,
        signature: paymentResponse.signature,
        organizationId: subscriptionData.organizationId,
        planId: subscriptionData.planId,
        billingInterval: subscriptionData.billingInterval,
        amount: subscriptionData.amount
      });

      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if Razorpay is available (WEB-ONLY)
   * @returns {boolean} Is Razorpay available
   */
  isPaymentAvailable() {
    return typeof window !== 'undefined' && window.Razorpay !== undefined;
  }
}

// Export singleton instance
export default new PaymentService();
